import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services/authService';
import type { AuthStore, User, LoginCredentials, RegisterData, AuthResponse } from '@/types';

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      initialized: false,
      loading: true,
      error: null,

      initialize: async () => {
        set({ loading: true, error: null });

        try {
          const auth = await authService.initialize();

          set({
            isAuthenticated: auth.isAuthenticated,
            user: auth.user,
            token: auth.token,
            initialized: true,
            loading: false,
            error: null,
          });
        } catch {
          authService.clearAuth();
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            initialized: true,
            loading: false,
            error: null,
          });
        }
      },

      checkAuth: async () => {
        await get().initialize();
      },

      login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
        set({ loading: true, error: null });
        
        try {
          const response = await authService.login(credentials);
          
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            loading: false,
            error: null,
          });
          return response;
        } catch (error: any) {
          const errorMessage = error.response?.data?.error
            || error.response?.data?.message
            || error.message
            || 'Login failed';
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            loading: false,
            error: errorMessage,
          });

          throw error;
        }
      },

      register: async (data: RegisterData): Promise<AuthResponse> => {
        set({ loading: true, error: null });
        
        try {
          const response = await authService.register(data);
          
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            loading: false,
            error: null,
          });
          return response;
        } catch (error: any) {
          const errorMessage = error.response?.data?.error
            || error.response?.data?.message
            || error.message
            || 'Registration failed';
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            loading: false,
            error: errorMessage,
          });

          throw error;
        }
      },

      logout: async () => {
        set({ loading: true, error: null });
        
        try {
          await authService.logout();
        } catch (error) {
          console.error('Logout failed:', error);
        } finally {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            loading: false,
            error: null,
          });
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Check if user has specific role
      hasRole: (role: string) => {
        const { user } = get();
        return user?.role === role;
      },

      // Check if user is curator
      isCurator: () => {
        const { user } = get();
        return user?.role === 'curator';
      },

      // Check if user is student
      isStudent: () => {
        const { user } = get();
        return user?.role === 'student';
      },

      updateProfile: async (profileData: Partial<User>): Promise<User> => {
        try {
          const updatedUser = await authService.updateProfile(profileData);
          
          set({
            user: updatedUser,
            error: null,
          });

          return updatedUser;
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || error.message || 'Profile update failed';
          
          set({
            error: errorMessage,
          });

          throw error;
        }
      },

      changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
        try {
          await authService.changePassword(currentPassword, newPassword);
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || error.message || 'Password change failed';
          
          set({
            error: errorMessage,
          });

          throw error;
        }
      },

      refreshToken: async (): Promise<AuthResponse> => {
        set({ loading: true, error: null });

        try {
          const response = await authService.refreshToken();
          
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            loading: false,
            error: null,
          });

          return response;
        } catch (error: any) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            loading: false,
            error: error.message || 'Token refresh failed',
          });

          throw error;
        }
      },
    }),
    {
      name: 'teaching-app-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
      }),
    }
  )
);
